import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import toast from "components/partials/toast/toast";
import {
  cleanupFormDataForSave,
  getNestedValue,
  setNestedValue,
} from "containers/admin/clients/client/components/form-builder/form-builder";
import { getFormSchemaSidebarItems } from "containers/admin/clients/client/components/form-builder/form-schema";
import type { FormPageTab } from "containers/admin/clients/client/components/form-page-layout/form-page-layout";
import {
  createStrategyFormTabs,
  getStrategyData,
  isDynamicFormSchema,
} from "containers/admin/clients/client/programs-page/program-strategy-form.utils";
import { useCollegeProfileMarketingChannelsDescription } from "containers/admin/clients/client/components/client-strategy-page/use-college-profile-marketing-channels-description";

import { useClientStrategy, useUpsertClientStrategy } from "hooks/api/use-client-strategy";
import { useClientStrategySchema } from "hooks/api/use-client-strategy-schema";
import { useSyncedDraftData } from "hooks/use-synced-draft-data";
import { useTabDirtyState } from "hooks/use-tab-dirty-state";

import { ClientStrategyCategoryDTOStrategyCategory } from "generated/schemas";

export const useClientStrategyForm = (
  strategyCategory: ClientStrategyCategoryDTOStrategyCategory,
) => {
  const { clientId } = useParams<{ clientId: string }>();
  const [isEditing, setIsEditing] = useState(false);
  const [tabRevisions, setTabRevisions] = useState<Record<string, number>>({});
  const [isDraftReady, setIsDraftReady] = useState(false);

  const { data: schemaResponse, isLoading: isSchemaLoading } =
    useClientStrategySchema(strategyCategory);
  const { data: strategy, isLoading: isStrategyLoading } = useClientStrategy(
    clientId,
    strategyCategory,
  );
  const upsertStrategyMutation = useUpsertClientStrategy();

  const savedData = useMemo(() => getStrategyData(strategy?.data), [strategy?.data]);
  const resetKey = `${clientId ?? ""}:${strategyCategory}`;
  const { draftData, setDraftData, draftDataRef } = useSyncedDraftData({
    savedData,
    isEditing,
    resetKey,
  });

  const fieldOverrides = useCollegeProfileMarketingChannelsDescription();

  const tabs = useMemo(() => {
    if (!isDynamicFormSchema(schemaResponse)) {
      return [];
    }

    return createStrategyFormTabs(schemaResponse, fieldOverrides);
  }, [schemaResponse, fieldOverrides]);

  const sidebarItems = useMemo(() => {
    if (!isDynamicFormSchema(schemaResponse)) {
      return undefined;
    }

    return getFormSchemaSidebarItems(schemaResponse);
  }, [schemaResponse]);

  const { hasChanges } = useTabDirtyState({ tabs, draftData, savedData, isEditing });

  useEffect(() => {
    setIsDraftReady(false);
    setIsEditing(false);
  }, [clientId, strategyCategory]);

  useEffect(() => {
    if (isStrategyLoading || isSchemaLoading || !tabs.length || isEditing) {
      return;
    }

    setDraftData(savedData);
    setTabRevisions((prev) =>
      Object.fromEntries(tabs.map((tab) => [tab.id, (prev[tab.id] ?? 0) + 1])),
    );
    setIsDraftReady(true);
  }, [savedData, tabs, isStrategyLoading, isSchemaLoading, isEditing, setDraftData]);

  const getTabValues = (tab: FormPageTab) =>
    getStrategyData(getNestedValue(draftData, tab.dataPath ?? [tab.id]));

  const handleTabValuesChange = (tab: FormPageTab, values: Record<string, unknown>) => {
    const dataPath = tab.dataPath ?? [tab.id];
    setDraftData((prev) => setNestedValue(prev, dataPath, values));
  };

  const bumpTabRevisions = () => {
    setTabRevisions((prev) =>
      Object.fromEntries(tabs.map((tab) => [tab.id, (prev[tab.id] ?? 0) + 1])),
    );
  };

  const handleDiscard = () => {
    setDraftData(savedData);
    bumpTabRevisions();
  };

  const handleSubmit = (
    formData: Record<string, unknown>,
    tab: FormPageTab,
    saveSuccessTitle: string,
    saveErrorTitle: string,
  ) => {
    if (!clientId) {
      return;
    }

    const dataPath = tab.dataPath ?? [tab.id];
    const draftTabValues = getStrategyData(getNestedValue(draftDataRef.current, dataPath));
    const tabValues = cleanupFormDataForSave(tab.schema, { ...draftTabValues, ...formData });
    const nextData = setNestedValue(draftDataRef.current, dataPath, tabValues);

    upsertStrategyMutation.mutate(
      {
        clientId,
        strategyCategory,
        data: nextData,
      },
      {
        onSuccess: () => {
          toast.success({ data: { title: saveSuccessTitle } });
          setIsEditing(false);
        },
        onError: () => {
          toast.error({
            data: {
              title: saveErrorTitle,
              message: "Please try again.",
            },
          });
        },
      },
    );
  };

  const isPageReady =
    !isSchemaLoading && !isStrategyLoading && (tabs.length === 0 || isDraftReady);

  return {
    clientId,
    tabs,
    sidebarItems,
    isSchemaLoading,
    isStrategyLoading,
    isPageReady,
    isEditing,
    setIsEditing,
    hasChanges,
    tabRevisions,
    getTabValues,
    handleTabValuesChange,
    handleDiscard,
    handleSubmit,
    isSaving: upsertStrategyMutation.isPending,
  };
};
